import ExternalIntegrationLink from 'components/ExternalIntegrationLink';
import { ExternalLinkIcon } from 'components/icons';
import { Tooltip } from 'components/ui/tooltip';
import { t } from 'utils/getTranslations';

/** A stored DMS link as an absolute URL: one without a scheme opens as a site. */
export const normalizeDmsLink = (link: string): string => {
    if (!link) return '';
    if (link.match(/^https?:\/\//)) return link;
    if (link.match(/^\/\//)) return `https:${link}`;
    return `https://${link}`;
};

/**
 * A stored DMS link as a new-design read row's value: the address on one line, cut short before its glyph. A
 * function, not a component, so that the row's content is the `<a>` a read cell takes for one line of text.
 */
export const dmsLinkValue = (link: string) => (
    <a
        href={normalizeDmsLink(link)}
        target="_blank"
        rel="noopener noreferrer"
        className="text-dms flex min-w-0 items-center gap-1.5 no-underline hover:underline"
    >
        <span className="truncate">{link}</span>
        <ExternalLinkIcon className="size-3.5 flex-none" />
    </a>
);

/**
 * A record's document-system folder, opened in a new tab. `link` is the "DMS ↗" text link; `icon-chip`
 * is the new design's 22px tinted "DMS ↗" pill beside a title, with "Open in DMS" as its tooltip.
 */
export default function DmsLink({
    link,
    isIconDisplayed,
    label,
    variant = 'link',
}: {
    link: string;
    isIconDisplayed?: boolean;
    label?: string;
    variant?: 'link' | 'icon-chip';
}) {
    const normalizedLink = normalizeDmsLink(link);

    if (variant === 'icon-chip') {
        return (
            <Tooltip content={t('open-in-dms')}>
                <a
                    href={normalizedLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-dms-soft text-dms text-meta flex h-[22px] shrink-0 items-center gap-1 rounded-[5px] px-1.5 font-semibold whitespace-nowrap"
                >
                    DMS
                    <ExternalLinkIcon className="size-[13px]" />
                </a>
            </Tooltip>
        );
    }

    return <ExternalIntegrationLink href={normalizedLink} label={label || 'DMS'} showIcon={isIconDisplayed} />;
}
